import React, { useMemo, useState } from 'react';
import { contractCode } from '../../utils/positionLedger';
import { LadderContract, LadderSortKey, sortContracts } from '../../utils/contractLadder';
import { LIQUIDITY_RULE, useLiquidityRule } from '../../utils/liquidityRule';
import { fmtPlainDate } from '../../utils/plainDate';
import { StrikeGlyph } from '../common/Glyphs';
import type { ExpiryGroupFacts } from '../../types';

export interface LadderRow<C extends LadderContract = LadderContract> {
  key: string;
  contract: C | null;
  /** Picks only: "Conservative" / "Balanced" / "Aggressive". */
  profile?: string;
  /** Why a pick has no contract (shown in place of the figures). */
  unavailable?: string | null;
}

interface Props<C extends LadderContract> {
  rows: LadderRow<C>[];
  price: number | null;
  isPut: boolean;
  earningsDate?: string | null;
  /** Per-expiry facts from the backend (chain layout headers). */
  expiries?: ExpiryGroupFacts[];
  /** The row the user is looking at; gets the accent. */
  selectedKey?: string | null;
  onSelect?: (key: string) => void;
  actionLabel: string;
  onAction: (contract: C) => void;
  /** Column headers sort the rows (the all-contracts browser). */
  sortable?: boolean;
  /** Narrow container (next to the filter sidebar): drop the optional columns. */
  compact?: boolean;
  /** Option-chain layout: one collapsible section per expiry, strikes in order,
   *  a marker row at the stock price. Replaces column sorting. */
  chain?: boolean;
  ariaLabel: string;
}

const COLS: Array<{ key: LadderSortKey | null; label: string; title?: string; cls?: string }> = [
  { key: 'expiry', label: 'Contract' },
  { key: 'dte', label: 'DTE' },
  { key: 'otm', label: 'OTM', title: 'Distance from the stock price to the strike' },
  { key: 'delta', label: 'Δ' },
  { key: 'premium', label: 'Last', title: 'Last traded price. Yield, credit and cushion use it.' },
  { key: null, label: 'Credit', title: 'Premium per contract (100 shares)', cls: 'cl-opt' },
  { key: 'aroc', label: 'Yield/yr', title: 'Premium yield per year: on the stock price for a call, on the strike for a put' },
  { key: 'protection', label: 'Cushion', title: 'How far the stock can fall before the position loses money', cls: 'cl-opt' },
  { key: 'iv', label: 'IV', cls: 'cl-opt' },
  { key: 'oi', label: 'OI' },
  { key: null, label: 'Traded', title: 'Day of the last trade (amber: before today\'s session)', cls: 'cl-opt' },
  { key: null, label: 'Flags' },
];

/**
 * Contract ladder (Oct 2026): one dense, sortable table for the analysis
 * page's picks and for every contract in the browser. Mono figures, flags as
 * text (EARN = earnings before expiry, THIN = outside the liquidity rule),
 * the selected row in the accent.
 */
function ContractLadder<C extends LadderContract>({
  rows, price, isPut, earningsDate, expiries, selectedKey, onSelect, actionLabel, onAction, sortable = false, compact = false, chain = false, ariaLabel,
}: Props<C>) {
  useLiquidityRule(); // re-render when the server liquidity rule arrives
  const [sort, setSort] = useState<{ key: LadderSortKey; dir: 1 | -1 }>({ key: 'expiry', dir: 1 });
  const hasProfile = rows.some((r) => r.profile);

  const ordered = useMemo(() => {
    if (!sortable) return rows;
    const withC = rows.filter((r) => r.contract) as Array<LadderRow<C> & { contract: C }>;
    const sorted = sortContracts(withC.map((r) => r.contract), sort.key, sort.dir);
    const byContract = new Map(withC.map((r) => [r.contract, r]));
    return sorted.map((c) => byContract.get(c)!);
  }, [rows, sortable, sort]);

  const header = (c: typeof COLS[number]) => {
    if (!sortable || chain || !c.key) return chain && c.key === 'expiry' ? 'Strike' : c.label;
    const on = sort.key === c.key;
    return (
      <button
        type="button"
        className={`cl-sort ${on ? 'is-on' : ''}`}
        onClick={() => setSort({ key: c.key!, dir: on ? (sort.dir === 1 ? -1 : 1) : c.key === 'expiry' || c.key === 'strike' ? 1 : -1 })}
      >
        {c.label}{on ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
      </button>
    );
  };

  const renderRow = (r: LadderRow<C>) => {
            const c = r.contract;
            const sel = selectedKey != null && r.key === selectedKey;
            if (!c) {
              return (
                <tr key={r.key} className="is-na">
                  {hasProfile && <td className="cl-l cl-prof">{r.profile}</td>}
                  <td className="cl-l cl-na" colSpan={COLS.length + 1}>{r.unavailable || 'No contract in this window.'}</td>
                </tr>
              );
            }
            const otm = c.otm_pct ?? null;
            const earn = !!c.earnings_inside;
            const thin = !!c.thin;
            const traded = fmtPlainDate(c.last_trade_date);
            return (
              <tr
                key={r.key}
                className={`${sel ? 'is-sel' : ''} ${onSelect ? 'is-click' : ''}`}
                onClick={onSelect ? () => onSelect(r.key) : undefined}
                aria-selected={onSelect ? sel : undefined}
              >
                {hasProfile && <td className="cl-l cl-prof">{r.profile}</td>}
                <td className="cl-l n cl-ct">{chain ? `${Number.isInteger(c.strike_price) ? c.strike_price : c.strike_price.toFixed(2).replace(/0$/, '')}${isPut ? 'P' : 'C'}` : contractCode(c.expiration_date, c.strike_price, isPut)}</td>
                {!chain && <td className="n">{c.days_to_expiration ?? '—'}</td>}
                <td className="n"><span className="cl-otm"><StrikeGlyph otmPct={otm} />{otm == null ? '—' : otm >= 0 ? `${otm.toFixed(1)}%` : <span className="cl-loss">ITM {Math.abs(otm).toFixed(1)}%</span>}</span></td>
                <td className="n">{c.delta == null ? '—' : Math.abs(c.delta).toFixed(2)}</td>
                <td className="n">{c.premium != null ? c.premium.toFixed(2) : '—'}</td>
                <td className="n cl-opt">{c.credit_per_contract != null ? Math.round(c.credit_per_contract).toLocaleString('en-US') : '—'}</td>
                <td className="n cl-strong">{c.aroc != null ? `${c.aroc.toFixed(1)}%` : '—'}</td>
                <td className="n cl-opt">{c.downside_protection != null ? `${c.downside_protection.toFixed(1)}%` : '—'}</td>
                <td className="n cl-opt">{c.implied_volatility == null ? '—' : `${c.implied_volatility.toFixed(0)}%`}</td>
                <td className={`n ${thin && c.open_interest != null && c.open_interest < LIQUIDITY_RULE.minOpenInterest ? 'cl-warn' : ''}`}>{c.open_interest != null ? c.open_interest.toLocaleString('en-US') : '—'}</td>
                <td className={`n cl-opt ${c.is_stale ? 'cl-warn' : 'cl-dim'}`}>{traded ?? '—'}</td>
                <td className="cl-l cl-flags">
                  {c.is_best && <span className="cl-flag is-liq" title="The stock's best contract: the highest-yield standard contract (delta 0.15–0.30, 30–45 days out, open interest ≥ 100). The screener's premium figure is this one.">BEST</span>}
                  {chain && topOi.has(c.contract_ticker ?? '') && <span className="cl-flag is-liq" title="Highest open interest in this expiry, inside the liquidity rule: usually the easiest fill">TOP OI</span>}
                  {!chain && earn && <span className="cl-flag" title={`Earnings ${fmtPlainDate(earningsDate) ?? ''} fall on or before this expiry`}>EARN</span>}
                  {thin && <span className="cl-flag" title={`Outside the liquidity rule (spread ≤ ${LIQUIDITY_RULE.maxSpreadPct}%, OI ≥ ${LIQUIDITY_RULE.minOpenInterest})`}>THIN</span>}
                  {c.tiny_premium && <span className="cl-flag" title="Premium under $0.16 a share: less than $15 a contract after a $1 commission">TINY</span>}
                </td>
                <td className="cl-act">
                  <button type="button" className={`cl-btn ${sel ? 'is-pri' : ''}`} onClick={(e) => { e.stopPropagation(); onAction(c); }}>
                    {actionLabel}
                  </button>
                </td>
              </tr>
            );
  };

  // ---- option chain: group by expiry, strikes ascending, price marker ----
  const groups = useMemo(() => {
    if (!chain) return [];
    const by = new Map<string, LadderRow<C>[]>();
    rows.forEach((r) => { if (r.contract) { const k = r.contract.expiration_date.slice(0, 10); by.set(k, [...(by.get(k) ?? []), r]); } });
    return [...by.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([exp, rs]) => ({ exp, rows: rs.sort((a, b) => a.contract!.strike_price - b.contract!.strike_price) }));
  }, [chain, rows]);
  // Per-expiry facts from the backend: read-time DTE, monthly / weekly, earnings
  // inside, low liquidity, the highest yield and the deepest strike inside the rule.
  const facts = useMemo(() => new Map((expiries ?? []).map((e) => [e.expiration_date, e])), [expiries]);
  const topOi = useMemo(() => new Set((expiries ?? []).map((e) => e.top_oi_contract).filter(Boolean) as string[]), [expiries]);
  const [open, setOpen] = useState<Set<string> | null>(null);
  const defaultExp = groups.length
    ? groups.reduce((best, g) => (Math.abs((g.rows[0].contract!.days_to_expiration ?? 999) - 35) < Math.abs((best.rows[0].contract!.days_to_expiration ?? 999) - 35) ? g : best)).exp
    : null;
  const openSet = open ?? new Set(defaultExp ? [defaultExp] : []);
  // Strikes near the money by default (8 each side of the stock price); "show all" per expiry.
  const NEAR = 8;
  const [fullExp, setFullExp] = useState<Set<string>>(new Set());
  const toggle = (exp: string) => setOpen(() => { const n = new Set(openSet); n.has(exp) ? n.delete(exp) : n.add(exp); return n; });
  const span = COLS.length + (hasProfile ? 1 : 0) + (chain ? 0 : 1);
  const renderChain = () => groups.map(({ exp, rows: rs }) => {
    const f = facts.get(exp);
    const dte = f?.days_to_expiration ?? rs[0].contract!.days_to_expiration;
    const monthly = f?.expiry_cycle === 'monthly';
    const isOpen = openSet.has(exp);
    const out: React.ReactNode[] = [
      <tr key={`h-${exp}`} className={`cl-grp ${isOpen ? 'is-open' : ''}`}>
        <td colSpan={span}>
          <button type="button" className="cl-grp-btn" aria-expanded={isOpen} onClick={() => toggle(exp)}>
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 4 4 4-4 4" /></svg>
            <b className="cl-grp-date">{fmtPlainDate(exp, { weekday: 'short', month: 'short', day: 'numeric' })}</b>
            <span className="n cl-grp-dte">{dte != null ? `${dte}d` : '—'}</span>
            {f?.expiry_cycle && (
              <span className={`cl-kind ${monthly ? 'is-m' : ''}`} title={monthly ? 'Standard monthly expiry (third Friday): usually the most liquid' : 'Weekly expiry: usually thinner open interest and wider spreads'}>{monthly ? 'Monthly' : 'Weekly'}</span>
            )}
            {f?.earnings_inside && earningsDate && (
              <span className="cl-earn" title="Earnings fall on or before this expiry: the stock can gap on the report">
                Earnings {fmtPlainDate(earningsDate)} inside
              </span>
            )}
            {f?.low_liquidity && (
              <span className="cl-earn" title={f.max_open_interest != null ? `Highest open interest in this expiry is ${f.max_open_interest}, below ${LIQUIDITY_RULE.minOpenInterest}: fills may be hard` : 'Open interest unknown for this expiry'}>Low liquidity</span>
            )}
            {f?.max_yield != null && <span className="cl-grp-m">up to <span className="n">{f.max_yield.toFixed(1)}%</span>/yr</span>}
          </button>
        </td>
      </tr>,
    ];
    if (isOpen) {
      let marked = price == null;
      const atm = price == null ? 0 : Math.max(0, rs.findIndex((r) => r.contract!.strike_price >= (price as number)));
      const atmIdx = price != null && rs.every((r) => r.contract!.strike_price < (price as number)) ? rs.length : atm;
      const full = fullExp.has(exp) || rs.length <= NEAR * 2 + 2;
      const lo = full ? 0 : Math.max(0, atmIdx - NEAR);
      const hi = full ? rs.length : Math.min(rs.length, atmIdx + NEAR);
      const hidden = rs.length - (hi - lo);
      rs.slice(lo, hi).forEach((r) => {
        if (!marked && r.contract!.strike_price >= (price as number)) {
          out.push(<tr key={`px-${exp}`} className="cl-px"><td colSpan={span}><span>Stock <b className="n">{(price as number).toFixed(2)}</b></span></td></tr>);
          marked = true;
        }
        out.push(renderRow(r));
      });
      if (!marked) out.push(<tr key={`px-${exp}`} className="cl-px"><td colSpan={span}><span>Stock <b className="n">{(price as number).toFixed(2)}</b></span></td></tr>);
      if (hidden > 0 || fullExp.has(exp)) {
        out.push(
          <tr key={`more-${exp}`} className="cl-more">
            <td colSpan={span}>
              <button type="button" onClick={() => setFullExp((f) => { const n = new Set(f); n.has(exp) ? n.delete(exp) : n.add(exp); return n; })}>
                {fullExp.has(exp) ? 'Show strikes near the money' : `Show all ${rs.length} strikes (${hidden} further from the money)`}
              </button>
            </td>
          </tr>,
        );
      }
    }
    return out;
  });

  return (
    <div className={`cl-wrap ${compact ? 'is-compact' : ''} ${onSelect ? 'has-select' : ''}`}>
      <table className="cl" aria-label={ariaLabel}>
        <thead>
          <tr>
            {hasProfile && <th className="cl-l">Profile</th>}
            {COLS.filter((c) => !(chain && c.key === 'dte')).map((c, i) => (
              <th
                key={c.label}
                className={`${i === 0 || c.label === 'Flags' ? 'cl-l' : ''} ${c.cls ?? ''}`}
                title={c.title}
                aria-sort={sortable && c.key && sort.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
              >
                {header(c)}
              </th>
            ))}
            <th aria-label="Action" />
          </tr>
        </thead>
        <tbody>
          {chain ? renderChain() : ordered.map((r) => renderRow(r))}
        </tbody>
      </table>
    </div>
  );
}

export default ContractLadder;
